import { useState } from "react";
import { toast } from "react-toastify";
import { useStore } from "../../../helpers";
import {
    requireVoterAndProject,
    runFaceCheck,
    runFaceDecision,
    uploadVoterPhoto,
} from "./voter-photo-upload";
import { toFaceCheckError } from "./sr-face-check-api";
import type { FaceSearchError } from "./face-search-errors";
import type {
    SrFaceCheckResult,
    SrFaceDecision,
    VoterPhotoUploaded,
} from "./sr-face-check-types";

type Args = {
    voterId: string;
    projectId?: number | string | null;
    onUploaded: (result: VoterPhotoUploaded) => void;
};

export const useVoterPhotoFaceFlow = ({ voterId, projectId, onUploaded }: Args) => {
    const rootStore = useStore();
    const [isBusy, setIsBusy] = useState(false);
    const [uploadedPath, setUploadedPath] = useState<string | null>(null);
    const [faceCheck, setFaceCheck] = useState<SrFaceCheckResult | null>(null);
    const [faceError, setFaceError] = useState<FaceSearchError | null>(null);

    const resetFlow = () => {
        setUploadedPath(null);
        setFaceCheck(null);
        setFaceError(null);
        setIsBusy(false);
    };

    const clearError = () => setFaceError(null);

    const finish = (photoUrl: string, extra?: Partial<VoterPhotoUploaded>) => {
        onUploaded({ photoUrl, ...extra });
        toast.success("Voter photo saved successfully.");
        resetFlow();
        return true;
    };

    const uploadAndCheck = async (capturedImage: string) => {
        if (!requireVoterAndProject(voterId, projectId)) return false;
        try {
            setIsBusy(true);
            setFaceError(null);
            setFaceCheck(null);
            const photoUrl = await uploadVoterPhoto(capturedImage, voterId, projectId!);
            setUploadedPath(photoUrl);
            const check = await runFaceCheck(rootStore.api, projectId!, photoUrl, voterId);
            setFaceCheck(check);
            return check.matches?.length ? false : finish(photoUrl);
        } catch (error) {
            console.error("Voter photo face check failed:", error);
            setFaceError(toFaceCheckError(error));
            return false;
        } finally {
            setIsBusy(false);
        }
    };

    const applyDecision = async (matchedVoterId: string, decision: SrFaceDecision) => {
        if (!uploadedPath || !projectId) return false;
        try {
            setIsBusy(true);
            setFaceError(null);
            const decided = await runFaceDecision(
                rootStore.api,
                projectId,
                uploadedPath,
                matchedVoterId,
                decision
            );
            return finish(uploadedPath, {
                decision,
                matchedVoterId,
                voter: decided.voter,
                photos: decided.photos,
            });
        } catch (error) {
            console.error("Face decision failed:", error);
            setFaceError(toFaceCheckError(error));
            return false;
        } finally {
            setIsBusy(false);
        }
    };

    return {
        isBusy,
        faceCheck,
        faceError,
        resetFlow,
        clearError,
        uploadAndCheck,
        applyDecision,
        continueWithoutMatch: () => (uploadedPath ? finish(uploadedPath) : false),
    };
};
